import { fetchWithAuth } from './http';
import { PAYMENTS_SERVICE_BASE_URL } from '../config/runtime';

export type Tier = 'free' | 'basic' | 'pro';

export interface SubscriptionInfo {
  tier: Tier;
  status?: string;
  priceId?: string;
  subscriptionId?: string;
  currentPeriodEnd?: string;
  provider?: string;
  raw?: unknown;
}

// Hardcoded price->tier fallback. Prefer the backend `tier` field; this only
// covers older records. (Tracked to remove once backend always returns tier.)
const PRICE_TO_TIER: Record<string, Tier> = {};

function inferTier(sub: any): Tier {
  if (!sub) return 'free';
  const status = String(sub.status ?? '').toLowerCase();
  const hasId = Boolean(sub.subscriptionId ?? sub.id);
  const paidStatuses = ['active', 'trialing', 'complimentary', 'paid'];

  if (!paidStatuses.includes(status) && !hasId) return 'free';
  if (status === 'complimentary' || sub.provider === 'admin') return 'pro';

  const backendTier = String(sub.tier ?? sub.tierFromBackend ?? '').toLowerCase();
  if (backendTier === 'pro' || backendTier === 'all_gas') return 'pro';
  if (backendTier === 'basic' || backendTier === 'cruise') return 'basic';

  const alias = String(sub.planAlias ?? '').toLowerCase();
  if (alias.includes('pro') || alias.includes('premium')) return 'pro';
  if (alias.includes('basic')) return 'basic';

  const priceId = sub.priceId ?? sub.price?.id;
  if (priceId && PRICE_TO_TIER[priceId]) return PRICE_TO_TIER[priceId];

  const amount = Number(sub.amount ?? sub.price?.unitAmount ?? 0);
  if (amount >= 1500) return 'pro';
  if (amount > 0) return 'basic';

  return hasId ? 'pro' : 'free';
}

// Primary source is PaymentsService; falls back to the data-service mirror.
export async function getSubscription(userId: string): Promise<SubscriptionInfo> {
  try {
    const res = await fetchWithAuth(`${PAYMENTS_SERVICE_BASE_URL}/subscriptions/subscription`);
    if (res.ok) {
      const data = await res.json();
      const sub = data.subscription ?? data;
      if (sub && (sub.subscriptionId || sub.id || sub.tier || sub.status)) {
        return { tier: inferTier(sub), status: sub.status, priceId: sub.priceId, subscriptionId: sub.subscriptionId, currentPeriodEnd: sub.currentPeriodEnd, provider: sub.provider, raw: sub };
      }
    }
  } catch {
    /* fall through to mirror */
  }

  try {
    const res = await fetchWithAuth(`/api/users/${encodeURIComponent(userId)}/subscription`);
    if (res.ok) {
      const sub = await res.json();
      return { tier: inferTier(sub), status: sub.status, provider: sub.provider, raw: sub };
    }
  } catch {
    /* free */
  }
  return { tier: 'free' };
}

// ── Pay-per-item (single premium generation without a subscription) ──
// TAG BE/PaymentsService: these endpoints do NOT exist yet. See plan BE4/PaymentsService updates.
export interface PayPerItemQuote {
  sku: string;
  amount: number;
  currency: string;
}

export async function createPayPerItemCheckout(_sku: string): Promise<{ clientSecret?: string; url?: string }> {
  // Placeholder: intended to call PaymentsService POST /payments/checkout-session
  // with a one-off price for a single premium generation. Returns a Stripe
  // Checkout URL or PaymentIntent client secret once implemented.
  throw new Error('Pay-per-item checkout is not available yet (PaymentsService update pending).');
}
